import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Paywall from "@/components/Paywall";

const STEPS = [
  { key: "hero", title: "1. Choisis le héros", options: ["Un renard curieux", "Une princesse astronome", "Un dragon rigolo", "Un petit robot gentil", "Une tortue rapide"] },
  { key: "setting", title: "2. Choisis le décor", options: ["La forêt enchantée", "Un château sur la lune", "L'école des magiciens", "Le fond de l'océan", "Une ferme pleine d'animaux"] },
  { key: "theme", title: "3. Choisis la leçon", options: ["Le partage", "Le courage", "La curiosité", "L'amitié", "La patience"] },
];

function Step({ step, value, onChange, delay }) {
  const custom = value && !step.options.includes(value);
  return (
    <section className="rise card-paper p-6" style={{ animationDelay: `${delay}ms` }}>
      <h2 className="font-display text-2xl font-bold">{step.title}</h2>
      <div className="mt-4 flex flex-wrap gap-2">
        {step.options.map((o, i) => (
          <button key={o} type="button" data-testid={`gen-${step.key}-option-${i}`} onClick={() => onChange(o)}
            className={`chip !px-4 !py-2 !text-base ${value === o ? "chip-active" : ""}`}>{o}</button>
        ))}
      </div>
      <input data-testid={`gen-${step.key}-custom-input`} placeholder="…ou écris ta propre idée" value={custom ? value : ""}
        onChange={(e) => onChange(e.target.value)} maxLength={100}
        className="mt-4 w-full rounded-2xl border-2 border-dashed border-[color:var(--line)] bg-transparent px-4 py-2.5 outline-none focus:border-[color:var(--terracotta)]" />
    </section>
  );
}

export default function Generator() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ hero: "", setting: "", theme: "", age_group: (user.child_age || 6) <= 5 ? "3-5" : "6-8", child_name: user.child_name || "" });
  const [busy, setBusy] = useState(false);
  const ready = form.hero.length > 1 && form.setting.length > 1 && form.theme.length > 1;

  if (!user.is_premium) return (
    <main className="px-4 py-16"><Paywall title="Le Générateur est réservé à Premium" text="Avec Premium, inventez jusqu'à 10 histoires par mois, écrites et illustrées par l'IA avec le prénom de votre enfant." /></main>
  );

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/stories/generate", form);
      refreshUser();
      navigate(`/histoire/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  return (
    <main data-testid="generator-page" className="mx-auto max-w-4xl px-4 pb-24 pt-10 sm:px-8">
      <h1 className="rise font-display text-4xl font-bold sm:text-5xl">Le Générateur Sur-Mesure</h1>
      <p className="rise mt-3 text-lg text-[color:var(--muted-ink)]">Invente une histoire unique, écrite et illustrée par l'IA.</p>
      <p data-testid="gen-quota" className="rise mt-3 inline-block rounded-full bg-[color:var(--paper-2)] px-4 py-1.5 font-bold">
        {user.ai_stories_left ?? 10} histoire{(user.ai_stories_left ?? 10) > 1 ? "s" : ""} IA restante{(user.ai_stories_left ?? 10) > 1 ? "s" : ""} ce mois-ci
      </p>
      <div className="mt-8 space-y-5">
        {STEPS.map((s, i) => <Step key={s.key} step={s} delay={i * 80} value={form[s.key]} onChange={(v) => setForm({ ...form, [s.key]: v })} />)}
        <section className="rise card-paper grid gap-5 p-6 sm:grid-cols-2" style={{ animationDelay: "240ms" }}>
          <div>
            <h2 className="font-display text-2xl font-bold">4. Pour quel âge ?</h2>
            <div className="mt-4 flex gap-2">
              {[["3-5", "3 – 5 ans"], ["6-8", "6 – 8 ans"]].map(([v, l]) => (
                <button key={v} type="button" data-testid={`gen-age-${v}`} onClick={() => setForm({ ...form, age_group: v })}
                  className={`chip !px-4 !py-2 !text-base ${form.age_group === v ? "chip-active" : ""}`}>{l}</button>
              ))}
            </div>
          </div>
          <label className="block">
            <h2 className="font-display text-2xl font-bold">5. Le prénom de l'ami</h2>
            <input data-testid="gen-child-name-input" value={form.child_name} maxLength={40} onChange={(e) => setForm({ ...form, child_name: e.target.value })}
              className="mt-4 w-full rounded-2xl border-2 border-[color:var(--line)] px-4 py-2.5 outline-none focus:border-[color:var(--terracotta)]" placeholder="Optionnel" />
          </label>
        </section>
      </div>
      <button data-testid="gen-submit-button" disabled={!ready || busy} onClick={submit} className="btn-primary mt-8 w-full !py-4 !text-xl">
        <Wand2 /> {busy ? "C'est parti…" : "Créer mon histoire magique"}
      </button>
    </main>
  );
}
